import type { SvgIconComponent } from '@mui/icons-material';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import MiscellaneousServicesOutlinedIcon from '@mui/icons-material/MiscellaneousServicesOutlined';
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import FolderSharedOutlinedIcon from '@mui/icons-material/FolderSharedOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import type { Permissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import type { Ruolo } from '@/types';

/** Chiave di un badge risolto da `useSidebarBadges`. */
export type SidebarBadgeKey =
  | 'openTickets'
  | 'backupPending'
  | 'unreadNotifications'
  | 'machineAlerts'
  | 'preventiviDaInviare';

export type SidebarActionId = 'logout';

export type NavContext = 'tenant' | 'staff' | 'any';

type VisibleWhen = (permissions: Permissions) => boolean;

interface Visibility {
  visibleWhen: VisibleWhen;
  context: NavContext;
}

/** Voce foglia: naviga a un path esistente. Senza icona se è figlia di un gruppo. */
export interface SidebarLinkItem extends Visibility {
  id: string;
  kind: 'link';
  label: string;
  to: string;
  icon?: SvgIconComponent;
  /** `true` solo per le radici (`/` e `/admin`) che altrimenti matcherebbero ogni sotto-path. */
  exact?: boolean;
  badge?: SidebarBadgeKey;
  shortcut?: string;
}

export interface SidebarActionItem extends Visibility {
  id: string;
  kind: 'action';
  label: string;
  action: SidebarActionId;
  icon: SvgIconComponent;
}

export interface SidebarGroupItem extends Visibility {
  id: string;
  kind: 'group';
  label: string;
  icon: SvgIconComponent;
  /** True when at least two children stay visible — micro-tendina. */
  collapsible: boolean;
  defaultOpen: boolean;
  children: SidebarLinkItem[];
}

export type SidebarEntry = SidebarLinkItem | SidebarActionItem | SidebarGroupItem;

export interface SidebarSection extends Visibility {
  id: 'primary' | 'operativita' | 'configurazione' | 'amministrazione' | 'account';
  /** `null`: nessuna etichetta visibile (area di lavoro sempre in cima). */
  label: string | null;
  /** Etichetta accessibile del `List`. */
  ariaLabel: string;
  entries: SidebarEntry[];
}

const staff: VisibleWhen = (p) => p.isStaff;
const tenantPortal: VisibleWhen = (p) => p.isTenantAdmin;
const tenantUser: VisibleWhen = (p) => p.isTenantUser;
const technicalStaff: VisibleWhen = (p) => p.canManageMacchine;
const ticketQueue: VisibleWhen = (p) => p.canViewTicketQueue;
const tasks: VisibleWhen = (p) => p.canViewTasks;
const ticketCategories: VisibleWhen = (p) => p.canManageTicketCategories;
const frameworkMatrix: VisibleWhen = (p) => p.canManageFrameworkMatrix;
const documenti: VisibleWhen = (p) => p.role !== null;
const documentiVisibilita: VisibleWhen = (p) =>
  p.canToggleVisibilitaWenkey || p.canToggleVisibilitaCliente;
const everyone: VisibleWhen = () => true;

/**
 * Informazione architecture della sidebar.
 *
 * Solo path già presenti in `paths.ts`. Le capability arrivano da `usePermissions`
 * e ricalcano i `RoleRoute` di `AppRoutes` — non inventiamo guardie nuove.
 */
export const sidebarNav: readonly SidebarSection[] = [
  {
    id: 'primary',
    label: null,
    ariaLabel: 'Area di lavoro',
    context: 'any',
    visibleWhen: everyone,
    entries: [
      {
        id: 'dashboard-operativa',
        kind: 'link',
        label: 'Dashboard operativa',
        to: paths.admin.operativa,
        icon: SpaceDashboardOutlinedIcon,
        exact: true,
        shortcut: 'g o',
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'panoramica',
        kind: 'link',
        label: 'Panoramica',
        to: paths.dashboard,
        icon: InsightsOutlinedIcon,
        exact: true,
        shortcut: 'g a',
        context: 'any',
        visibleWhen: (p) => p.isStaff || p.canViewCompanyHealth,
      },
      {
        id: 'servizi',
        kind: 'link',
        label: 'Servizi',
        to: paths.servizi.root,
        icon: HubOutlinedIcon,
        shortcut: 'g s',
        context: 'tenant',
        visibleWhen: tenantPortal,
      },
      {
        id: 'ticket',
        kind: 'link',
        label: 'Ticket',
        to: paths.ticket.root,
        icon: ConfirmationNumberOutlinedIcon,
        badge: 'openTickets',
        shortcut: 'g t',
        context: 'tenant',
        visibleWhen: tenantPortal,
      },
      {
        id: 'ticket-portale',
        kind: 'group',
        label: 'Ticket',
        icon: ConfirmationNumberOutlinedIcon,
        collapsible: true,
        defaultOpen: false,
        context: 'tenant',
        visibleWhen: tenantUser,
        children: [
          {
            id: 'ticket-aperti',
            kind: 'link',
            label: 'In lavorazione',
            to: paths.ticket.inLavorazione,
            icon: HourglassEmptyOutlinedIcon,
            exact: true,
            badge: 'openTickets',
            shortcut: 'g t',
            context: 'tenant',
            visibleWhen: tenantUser,
          },
          {
            id: 'ticket-nuovo',
            kind: 'link',
            label: 'Apri ticket',
            to: paths.ticket.nuovo,
            icon: AddCircleOutlineOutlinedIcon,
            exact: true,
            context: 'tenant',
            visibleWhen: tenantUser,
          },
          {
            id: 'ticket-chiusi',
            kind: 'link',
            label: 'Ticket chiusi',
            to: paths.ticket.chiusi,
            icon: HistoryOutlinedIcon,
            exact: true,
            context: 'tenant',
            visibleWhen: tenantUser,
          },
        ],
      },
      {
        id: 'calendario-portale',
        kind: 'link',
        label: 'Calendario',
        to: paths.calendario,
        icon: CalendarMonthOutlinedIcon,
        context: 'tenant',
        visibleWhen: (p) => p.isTenantAdmin || p.isTenantUser,
      },
      {
        id: 'report',
        kind: 'link',
        label: 'Report',
        to: paths.report.root,
        icon: AssessmentOutlinedIcon,
        context: 'any',
        visibleWhen: (p) => p.canViewCompanyHealth || p.canViewAggregatedReports,
      },
      {
        id: 'cedolini-portale',
        kind: 'link',
        label: 'Cedolini',
        to: paths.cedolini.root,
        icon: ReceiptLongOutlinedIcon,
        context: 'tenant',
        visibleWhen: (p) => p.isTenantAdmin && p.canViewPrices,
      },
      {
        id: 'preventivi-portale',
        kind: 'link',
        label: 'Preventivi',
        to: paths.preventivi.root,
        icon: RequestQuoteOutlinedIcon,
        context: 'tenant',
        visibleWhen: (p) => p.isTenantAdmin,
      },
      {
        id: 'contratti-portale',
        kind: 'link',
        label: 'Contratti',
        to: paths.contratti.root,
        icon: DescriptionOutlinedIcon,
        context: 'tenant',
        visibleWhen: (p) => p.isTenantAdmin && p.canViewPrices,
      },
      {
        id: 'documenti-portale',
        kind: 'group',
        label: 'Wiki',
        icon: FolderSharedOutlinedIcon,
        collapsible: true,
        defaultOpen: true,
        context: 'tenant',
        visibleWhen: (p) => p.isTenantAdmin || p.isTenantUser,
        children: [
          {
            id: 'documenti-portale-elenco',
            kind: 'link',
            label: 'Documenti',
            to: paths.documenti.root,
            icon: FolderSharedOutlinedIcon,
            context: 'tenant',
            visibleWhen: (p) => p.isTenantAdmin || p.isTenantUser,
          },
          {
            id: 'documenti-portale-visibilita',
            kind: 'link',
            label: 'Impostazioni',
            to: paths.documenti.visibilita,
            icon: SettingsOutlinedIcon,
            context: 'tenant',
            visibleWhen: (p) => p.canToggleVisibilitaCliente,
          },
        ],
      },
      {
        id: 'organizzazione',
        kind: 'link',
        label: 'Organizzazione',
        to: paths.organizzazione,
        icon: AccountTreeOutlinedIcon,
        context: 'tenant',
        visibleWhen: tenantPortal,
      },
      {
        id: 'utenti',
        kind: 'link',
        label: 'Utenti e ruoli',
        to: paths.utenti,
        icon: GroupOutlinedIcon,
        context: 'tenant',
        visibleWhen: tenantPortal,
      },
    ],
  },
  {
    id: 'operativita',
    label: 'Operatività',
    ariaLabel: 'Operatività',
    context: 'staff',
    visibleWhen: staff,
    entries: [
      {
        id: 'sicurezza-staff',
        kind: 'link',
        label: 'Sicurezza',
        to: paths.admin.sicurezza,
        icon: SecurityOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'lead-calendario',
        kind: 'link',
        label: 'Calendario',
        to: paths.admin.leadCalendario,
        icon: CalendarMonthOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'clienti',
        kind: 'group',
        label: 'Clienti',
        icon: BusinessOutlinedIcon,
        collapsible: true,
        defaultOpen: false,
        context: 'staff',
        visibleWhen: staff,
        children: [
          {
            id: 'clienti-elenco',
            kind: 'link',
            label: 'Clienti',
            to: paths.admin.clienti,
            icon: BusinessOutlinedIcon,
            shortcut: 'g c',
            context: 'staff',
            visibleWhen: staff,
          },
          {
            id: 'lead-elenco',
            kind: 'link',
            label: 'Lead',
            to: paths.admin.lead,
            icon: PersonSearchOutlinedIcon,
            context: 'staff',
            visibleWhen: staff,
          },
          {
            id: 'framework-matrix',
            kind: 'link',
            label: 'Matrice framework',
            to: paths.admin.frameworkMatrix,
            icon: HubOutlinedIcon,
            context: 'staff',
            visibleWhen: frameworkMatrix,
          },
        ],
      },
      {
        id: 'macchine',
        kind: 'group',
        label: 'Macchine',
        icon: DnsOutlinedIcon,
        collapsible: true,
        defaultOpen: false,
        context: 'staff',
        visibleWhen: staff,
        children: [
          {
            id: 'macchine-elenco',
            kind: 'link',
            label: 'Macchine',
            to: paths.admin.macchine,
            icon: DnsOutlinedIcon,
            context: 'staff',
            visibleWhen: technicalStaff,
          },
          {
            id: 'macchine-stato-salute',
            kind: 'link',
            label: 'Stato di salute',
            to: paths.admin.statoSalute,
            icon: MonitorHeartOutlinedIcon,
            context: 'staff',
            visibleWhen: staff,
          },
        ],
      },
      {
        id: 'backup',
        kind: 'group',
        label: 'Backup monitoring',
        icon: BackupOutlinedIcon,
        collapsible: true,
        defaultOpen: false,
        context: 'staff',
        visibleWhen: staff,
        children: [
          {
            id: 'backup-alert',
            kind: 'link',
            label: 'Alert',
            to: paths.admin.backup.alert,
            icon: WarningAmberOutlinedIcon,
            badge: 'machineAlerts',
            context: 'staff',
            visibleWhen: staff,
          },
          {
            id: 'backup-job',
            kind: 'link',
            label: 'Job backup',
            to: paths.admin.backup.jobs,
            icon: BackupOutlinedIcon,
            context: 'staff',
            visibleWhen: technicalStaff,
          },
          {
            id: 'backup-pending',
            kind: 'link',
            label: 'Da mappare',
            to: paths.admin.backup.pending,
            icon: BackupOutlinedIcon,
            badge: 'backupPending',
            context: 'staff',
            visibleWhen: staff,
          },
        ],
      },
      {
        id: 'attivita',
        kind: 'link',
        label: 'Attività',
        to: paths.admin.activities.list,
        icon: TaskAltOutlinedIcon,
        context: 'staff',
        visibleWhen: tasks,
      },
      {
        id: 'documenti-staff',
        kind: 'group',
        label: 'Wiki',
        icon: FolderSharedOutlinedIcon,
        collapsible: true,
        defaultOpen: false,
        context: 'staff',
        visibleWhen: documenti,
        children: [
          {
            id: 'documenti-elenco',
            kind: 'link',
            label: 'Documenti',
            to: paths.admin.documenti,
            icon: FolderSharedOutlinedIcon,
            context: 'staff',
            visibleWhen: documenti,
          },
          {
            id: 'documentazione-interna',
            kind: 'link',
            label: 'Documentazione interna',
            to: paths.admin.documentazioneInterna,
            icon: FolderOutlinedIcon,
            context: 'staff',
            visibleWhen: staff,
          },
          {
            id: 'documenti-visibilita',
            kind: 'link',
            label: 'Impostazioni',
            to: paths.admin.documentiVisibilita,
            icon: SettingsOutlinedIcon,
            context: 'staff',
            visibleWhen: documentiVisibilita,
          },
        ],
      },
    ],
  },
  {
    id: 'configurazione',
    label: 'Configurazione',
    ariaLabel: 'Configurazione',
    context: 'staff',
    visibleWhen: staff,
    entries: [
      {
        id: 'catalogo',
        kind: 'group',
        label: 'Catalogo',
        icon: Inventory2OutlinedIcon,
        collapsible: true,
        defaultOpen: false,
        context: 'staff',
        visibleWhen: staff,
        children: [
          {
            id: 'catalogo-prodotti',
            kind: 'link',
            label: 'Prodotti',
            to: paths.admin.catalog.products,
            icon: Inventory2OutlinedIcon,
            context: 'staff',
            visibleWhen: staff,
          },
          {
            id: 'catalogo-servizi',
            kind: 'link',
            label: 'Servizi',
            to: paths.admin.catalog.services,
            icon: MiscellaneousServicesOutlinedIcon,
            context: 'staff',
            visibleWhen: staff,
          },
          {
            id: 'categorie-ticket',
            kind: 'link',
            label: 'Categorie',
            to: paths.admin.categorie,
            icon: LabelOutlinedIcon,
            context: 'staff',
            visibleWhen: ticketCategories,
          },
        ],
      },
    ],
  },
  {
    id: 'amministrazione',
    label: 'Amministrazione',
    ariaLabel: 'Amministrazione',
    context: 'staff',
    visibleWhen: staff,
    entries: [
      {
        id: 'coda-ticket',
        kind: 'link',
        label: 'Coda ticket',
        to: paths.admin.codaTicket,
        icon: PlaylistAddCheckOutlinedIcon,
        badge: 'openTickets',
        shortcut: 'g t',
        context: 'staff',
        visibleWhen: ticketQueue,
      },
      {
        id: 'cedolini-admin',
        kind: 'link',
        label: 'Cedolini',
        to: paths.admin.cedolini,
        icon: ReceiptLongOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'contratti-elenco',
        kind: 'link',
        label: 'Contratti',
        to: paths.admin.contratti,
        icon: DescriptionOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'fatture-admin',
        kind: 'link',
        label: 'Fatture',
        to: paths.admin.fatture,
        icon: ReceiptOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'preventivi-mensili-admin',
        kind: 'link',
        label: 'Preventivi mensili',
        to: paths.admin.preventiviMensili,
        icon: RequestQuoteOutlinedIcon,
        badge: 'preventiviDaInviare',
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'preventivi-admin',
        kind: 'link',
        label: 'Preventivi',
        to: paths.admin.preventivi,
        icon: RequestQuoteOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
      {
        id: 'utenti-staff',
        kind: 'link',
        label: 'Utenti e ruoli',
        to: paths.admin.users,
        icon: GroupOutlinedIcon,
        context: 'staff',
        visibleWhen: staff,
      },
    ],
  },
  {
    id: 'account',
    label: 'Account',
    ariaLabel: 'Account',
    context: 'any',
    visibleWhen: everyone,
    entries: [
      {
        id: 'notifiche',
        kind: 'link',
        label: 'Notifiche',
        to: paths.notifiche,
        icon: NotificationsNoneOutlinedIcon,
        badge: 'unreadNotifications',
        context: 'any',
        visibleWhen: everyone,
      },
      {
        id: 'profilo',
        kind: 'link',
        label: 'Profilo',
        to: paths.impostazioni,
        icon: PersonOutlinedIcon,
        context: 'any',
        visibleWhen: everyone,
      },
      {
        id: 'vaultwarden',
        kind: 'link',
        label: 'Vaultwarden',
        to: paths.vaultwarden,
        icon: SettingsOutlinedIcon,
        context: 'staff',
        visibleWhen: (p) => p.canManageVaultwarden,
      },
      {
        id: 'logout',
        kind: 'action',
        label: 'Esci',
        action: 'logout',
        icon: LogoutOutlinedIcon,
        context: 'any',
        visibleWhen: everyone,
      },
    ],
  },
];

/** True se `pathname` appartiene alla voce (query string ignorata). */
export function isNavPathActive(pathname: string, to: string, exact?: boolean): boolean {
  const pathOnly = to.split('?')[0] ?? to;
  if (exact) return pathname === pathOnly;
  return pathname === pathOnly || pathname.startsWith(`${pathOnly}/`);
}

export function entryContainsPath(entry: SidebarEntry, pathname: string): boolean {
  if (entry.kind === 'link') return isNavPathActive(pathname, entry.to, entry.exact);
  if (entry.kind === 'action') return false;
  return entry.children.some((child) => isNavPathActive(pathname, child.to, child.exact));
}

export function collectLinkItems(sections: readonly SidebarSection[]): SidebarLinkItem[] {
  const links: SidebarLinkItem[] = [];
  for (const section of sections) {
    for (const entry of section.entries) {
      if (entry.kind === 'link') links.push(entry);
      else if (entry.kind === 'group') links.push(...entry.children);
    }
  }
  return links;
}

/** Voce attiva tra un elenco di foglie: match più specifico (path più lungo). */
export function activeItemId(pathname: string, items: readonly SidebarLinkItem[]): string | null {
  const matches = items.filter((item) => isNavPathActive(pathname, item.to, item.exact));
  if (matches.length === 0) return null;
  matches.sort((a, b) => b.to.length - a.to.length || Number(Boolean(b.exact)) - Number(Boolean(a.exact)));
  return matches[0]?.id ?? null;
}

/** Voce attiva: match più specifico (path più lungo), così `/servizi/backup` non evidenzia anche Servizi. */
export function activeLinkId(pathname: string, sections: readonly SidebarSection[]): string | null {
  return activeItemId(pathname, collectLinkItems(sections));
}

/**
 * Destinazioni della bottom navigation su `xs`.
 *
 * Lista curata per ruolo, max 5 voci (pollice). Se dopo i filtri visibilità
 * restano 0 o più di 5 voci, il chrome usa solo il Drawer.
 */
const BOTTOM_NAV_IDS: Record<Ruolo, readonly string[]> = {
  cliente_user: ['ticket-aperti', 'ticket-nuovo', 'ticket-chiusi', 'profilo'],
  cliente_admin: ['panoramica', 'servizi', 'ticket', 'preventivi-portale', 'cedolini-portale'],
  operatore: ['dashboard-operativa', 'coda-ticket', 'macchine-elenco', 'backup-pending', 'clienti-elenco'],
  operatore_amministrativo: ['clienti-elenco', 'contratti-elenco', 'preventivi-mensili-admin', 'cedolini-admin', 'utenti-staff'],
  admin: ['dashboard-operativa', 'coda-ticket', 'macchine-elenco', 'backup-pending', 'clienti-elenco'],
};

const BOTTOM_NAV_MAX_ITEMS = 5;

export function bottomNavItems(permissions: Permissions): SidebarLinkItem[] {
  if (!permissions.role) return [];
  const ids = BOTTOM_NAV_IDS[permissions.role];
  const byId = new Map(collectLinkItems(visibleSidebar(permissions)).map((item) => [item.id, item]));
  const items = ids
    .map((id) => byId.get(id))
    .filter((item): item is SidebarLinkItem => item != null && item.icon != null);
  if (items.length === 0 || items.length > BOTTOM_NAV_MAX_ITEMS) return [];
  return items;
}

function filterLink(item: SidebarLinkItem, permissions: Permissions): SidebarLinkItem | null {
  return item.visibleWhen(permissions) ? item : null;
}

function filterEntry(entry: SidebarEntry, permissions: Permissions): SidebarEntry | null {
  if (!entry.visibleWhen(permissions)) return null;

  if (entry.kind === 'group') {
    const children = entry.children.map((child) => filterLink(child, permissions)).filter((child): child is SidebarLinkItem => child != null);
    if (children.length === 0) return null;
    // A single visible child becomes a top-level link. Two or more stay a micro-tendina.
    if (children.length === 1 && children[0]) {
      const onlyChild = children[0];
      const promoted: SidebarLinkItem = {
        id: onlyChild.id,
        kind: 'link',
        label: onlyChild.label,
        to: onlyChild.to,
        icon: onlyChild.icon ?? entry.icon,
        exact: onlyChild.exact,
        badge: onlyChild.badge,
        shortcut: onlyChild.shortcut,
        context: onlyChild.context,
        visibleWhen: onlyChild.visibleWhen,
      };
      return promoted;
    }
    return { ...entry, collapsible: children.length >= 2, children };
  }

  return entry;
}

/**
 * Albero visibile per il ruolo corrente. Sezioni e gruppi senza voci spariscono.
 */
/** Staff panoramica lives on `/admin`; tenant admins stay on `/`. */
function destinationForRole(entry: SidebarEntry, permissions: Permissions): SidebarEntry {
  if (entry.kind === 'group') {
    return { ...entry, children: entry.children.map((child) => destinationForRole(child, permissions) as SidebarLinkItem) };
  }
  if (entry.kind === 'link' && entry.id === 'panoramica' && permissions.isStaff) {
    return { ...entry, to: paths.admin.dashboard, context: 'staff' };
  }
  return entry;
}

export function visibleSidebar(permissions: Permissions): SidebarSection[] {
  return sidebarNav
    .filter((section) => section.visibleWhen(permissions))
    .map((section) => ({
      ...section,
      entries: section.entries
        .map((entry) => filterEntry(entry, permissions))
        .filter((entry): entry is SidebarEntry => entry != null)
        .map((entry) => destinationForRole(entry, permissions)),
    }))
    .filter((section) => section.entries.length > 0);
}

/** Drop a leaf by id after role filtering, e.g. Organizzazione when the tenant has no structure. */
export function withoutNavEntry(sections: SidebarSection[], id: string): SidebarSection[] {
  return sections
    .map((section) => ({
      ...section,
      entries: section.entries.filter((entry) => entry.id !== id),
    }))
    .filter((section) => section.entries.length > 0);
}
